import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipDirective, TooltipModule } from 'ngx-bootstrap/tooltip';
import { Constants } from '../../../constants';
import { AlertService } from '../../../services';
import { FileHandle, UploadFileDirective } from '../../../directives/upload-file.directive';
import { acceptNoteFiles } from '../note-attachment.model';

/**
 * Pre-submit attachment selection for an internal note or reply.
 *
 * Everything that used to be copy-pasted per surface lives here once:
 * `handleFileInput`, `fileDropped`, `processDroppedFile`, `removeFile`, and each
 * surface's own slightly different size check. Eight copies of that, multiplied
 * by N files, is how this feature becomes eight bugs — one of which was already
 * shipped: the per-surface check was `Math.round(size / 1048576) >= 40`, which
 * rejects a legitimate 39.6MB file because it rounds to 40. `validateAttachments`
 * compares exact bytes, so a file of exactly 40MB is accepted and 40MB + 1 byte
 * is not.
 *
 * The selection is UNBOUNDED — any number of files, no combined-size ceiling.
 * The only thing that can turn a file away is its own size (or its type).
 *
 * It owns the selection and nothing else — no HTTP. The host submits the files
 * with whatever payload its own endpoint wants.
 *
 * Two TRIGGERS, on two different designs:
 *
 * `dropzone` is the NEW design — the reference dialog's `.drop` field, used by
 * the accounts and contacts add-note dialogs, and it renders the picked files as
 * the reference chip.
 *
 * `button` is the OLD design's paperclip, used only by `internal-notes`, whose
 * composer is a narrow editor with no room for a field. It renders the trigger
 * and nothing else: that page draws its own legacy file list, so the new chip
 * never appears on it. Its palette is literal, not tokenised — see the CSS.
 */
@Component({
  selector: 'app-note-attachment-picker',
  standalone: true,
  imports: [CommonModule, TooltipModule, UploadFileDirective],
  // Eager, matching the rest of this console — see internal-notes.
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './note-attachment-picker.component.html',
  styleUrl: './note-attachment-picker.component.css',
})
export class NoteAttachmentPickerComponent {
  /** The current selection. Never mutated — see `emit()`. */
  @Input() files: File[] = [];

  @Input() mode: 'dropzone' | 'button' = 'dropzone';

  /**
   * Dropzone mode, ONE-LINE variant: the glyph and a single muted sentence in a
   * row, instead of the stacked glyph / title / hint block.
   *
   * Opt-in, and only the sample-request MILESTONE note dialog asks for it —
   * console-v2 draws that dialog's field as a compact row (`.srd-drop`: 1px
   * dashed, 16px padding, 12.5px muted, all on one line) because it sits under
   * a 96px textarea inside a 440px confirm dialog, where the 26px two-line
   * block of the note dialogs' field would be the tallest thing in the box.
   * Everything the field DOES is unchanged — same multiple, same drop target,
   * same accept list, same size rule, same emit.
   */
  @Input() compact = false;

  /** Disables the control — e.g. while a note is saving. */
  @Input() disabled = false;

  /**
   * Attachments already SAVED on the note being edited, if any. They occupy
   * slots against the note's 10-attachment cap, so an editor opened on a note
   * that already holds 9 files may only add one more. A host composing a NEW
   * note or reply leaves it at 0.
   */
  @Input() existingCount = 0;

  @Output() filesChange = new EventEmitter<File[]>();

  /**
   * The button-mode input. Opened programmatically rather than by a wrapping
   * <label>, because the composer's paperclip is a <button> inside a flex cluster
   * the host owns.
   */
  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;

  /**
   * Unique id pairing the dropzone's <label for> with its sibling <input>.
   *
   * It must be per-INSTANCE: internal-notes renders one composer per expanded
   * note, so a hard-coded id would give every dropzone on the page the same
   * target and the first input would swallow every click.
   */
  readonly inputId = `na-pick-${NoteAttachmentPickerComponent.nextId++}`;
  private static nextId = 0;

  readonly accept = Constants.NOTE_ATTACHMENTS.ACCEPT;
  readonly dropHint = Constants.NOTE_ATTACHMENTS.DROP_HINT;
  readonly dropLineHint = Constants.NOTE_ATTACHMENTS.DROP_LINE_HINT;

  constructor(private alertService: AlertService) {}

  /** The paperclip's own tooltip, so `openPicker` can dismiss AND suppress it. */
  @ViewChild('uploadTip') private uploadTip?: TooltipDirective;

  /**
   * True from the moment the OS file dialog is opened until the pointer next
   * leaves or re-enters the paperclip. Bound to the tooltip's `[isDisabled]`,
   * which is the only thing that stops it being shown again — `show()` returns
   * early on it.
   *
   * Hiding the label on click is NOT enough here, which is what the previous
   * attempt got wrong. The OS file dialog takes the whole window's focus while
   * the paperclip keeps DOM focus, so closing it makes Chrome re-dispatch `focus`
   * to that button — and `focus` is half of ngx-bootstrap's default
   * `triggers: 'hover focus'`, so the label came straight back. It then had
   * nothing to close it: the pointer is over where the dialog's Open button was,
   * so no `mouseout` follows, and "Upload File" stayed painted over the composer.
   *
   * Suppressing rather than dropping the `focus` trigger keeps the label
   * reachable by keyboard, and is mechanism-independent — anything that tries to
   * re-show it in that window is refused, not just the focus restore.
   */
  tipSuppressed = false;

  /** Button mode: the control is a <button>, so the input is opened by hand. */
  openPicker(): void {
    if (this.disabled) return;
    // Order matters: hide the label that is already up, THEN close the door
    // behind it, then open the dialog that would otherwise re-open it.
    this.uploadTip?.hide();
    this.tipSuppressed = true;
    this.fileInputRef?.nativeElement.click();
  }

  /**
   * The paperclip is hoverable again — a real pointer crossing, which is the one
   * signal that cannot be produced by the file dialog closing.
   *
   * Deliberately NOT released on `blur`: Chrome blurs the focused element when
   * the window loses focus, so releasing there would re-arm the tooltip just in
   * time for the focus restore to show it, which is the bug.
   */
  releaseTip(): void {
    this.tipSuppressed = false;
  }

  /** Picked from the `<input type="file" multiple>`. */
  onInputChange(event: any): void {
    const input = event?.target as HTMLInputElement;
    this.add(input?.files);
    // Clearing the value is what lets the same file be re-picked after a remove —
    // without it the input's value is unchanged and `change` never fires again.
    if (input) input.value = '';
  }

  /** Dropped on the `appUploadFile [multiple]="true"` target. */
  onDropped(handles: FileHandle[]): void {
    this.add((handles ?? []).map((h) => h.file));
  }

  /**
   * Files that arrived from OUTSIDE this component's template — the Add/Edit
   * Note dialog's note editor is a drop target too, and it sits above the
   * dropzone rather than inside it.
   *
   * It exists so that drop cannot grow a second validation path: everything
   * still goes through `add()`, so a file dropped on the editor gets the same
   * 40MB cap, the same duplicate suppression against what is already picked,
   * and the same single grouped toast as one chosen in the field. Same reason
   * `internal-notes.fileDropped` routes through `acceptNoteFiles` rather than
   * checking sizes itself.
   */
  acceptFiles(picked: ArrayLike<File> | null | undefined): void {
    this.add(picked);
  }

  removeAt(index: number): void {
    if (index < 0 || index >= this.files.length) return;
    const next = this.files.slice();
    next.splice(index, 1);
    this.emit(next);
  }

  /**
   * The one validation path. Rejections surface as ONE toast for the whole batch
   * (`buildBlockedAttachmentsMessage` groups them by reason) rather than one per
   * file — dropping five `.exe`s should not stack five toasts.
   *
   * Duplicate suppression comes free from `fileSignature()` in the util: picking
   * then dropping the same file is a no-op with an "Already attached" clause, not
   * a second copy.
   */
  private add(picked: ArrayLike<File> | null | undefined): void {
    if (this.disabled || !picked || picked.length === 0) return;

    const { files, error } = acceptNoteFiles(picked, this.files, this.existingCount);
    if (error) {
      this.alertService.error(error);
    }
    if (files !== this.files) {
      this.emit(files);
    }
  }

  /** A NEW array every time, so a host holding it behind `OnPush` re-renders. */
  private emit(next: File[]): void {
    this.files = next;
    this.filesChange.emit(next);
  }
}
